import { Grid } from '@material-ui/core';
import { CashInCashOutMethod, CashInCashOutType, getCashInCashOutMethodById, Loan } from '@nemuru-eng/js-api-client';

import { DetailsDataSkeleton } from '@/components/modules/AssetDetails/components/shared/DetailsDataSkeleton';
import { BankAccountDetailsEditDialog } from '@/components/modules/BorrowerAccountDetails/components/BankAccountDetailsEditDialog';
import { BankAccountDetailsForm } from '@/components/modules/BorrowerAccountDetails/components/BankAccountDetailsForm';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';

interface BorrowerCashInCashOutDetailsProps {
  loan: Loan;
  editModeAllowed?: boolean;
}

export const BorrowerCashInCashOutDetails = ({ loan, editModeAllowed }: BorrowerCashInCashOutDetailsProps) => {
  const { opened, toggleDialog, setOpened } = useDialog();

  const borrowerCashInCashOutMethodId = loan.borrowerCashInCashOutMethodId;

  if (!borrowerCashInCashOutMethodId)
    return <PendingDataWarning type={PendingDataType.BORROWER_FINANCIAL_INFORMATION} />;

  const {
    data: cashInCashOutMethod,
    revalidate: revalidateCashInCashOutMethod,
    loading,
  } = useCache(
    getCashInCashOutMethodById,
    {
      id: borrowerCashInCashOutMethodId,
    },
    { fallbackData: undefined },
  );

  const getModuleByCashInCashOutMethodType = (cashInCashOutMethod: CashInCashOutMethod) => {
    switch (cashInCashOutMethod.type) {
      case CashInCashOutType.BANK_ACCOUNT:
      case CashInCashOutType.SAVING_BANK_ACCOUNT:
        return <BankAccountDetailsForm cashInCashOutMethod={cashInCashOutMethod} />;
      default:
        return <></>;
    }
  };

  const toggleEditModeDialog = editModeAllowed && cashInCashOutMethod ? toggleDialog : undefined;

  if (loading)
    return (
      <SubSectionWrapper title={'PAYMENT_METHOD'} mb={0}>
        <DetailsDataSkeleton numberOfRows={5} pt={1} />
      </SubSectionWrapper>
    );

  return (
    <>
      <SubSectionWrapper title={'PAYMENT_METHOD'} toggleEditModeDialog={toggleEditModeDialog}>
        {!cashInCashOutMethod ? (
          <PendingDataWarning type={PendingDataType.BORROWER_FINANCIAL_INFORMATION} />
        ) : (
          <>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <ReadOnlyDataRow
                  underlined
                  labelTextId={'CASH_IN_CASH_OUT_METHOD_TYPE'}
                  value={<Text tId={cashInCashOutMethod.type} />}
                />
                <ReadOnlyDataRow
                  underlined
                  labelTextId={'HOLDER'}
                  value={`${cashInCashOutMethod.accountHolderFirstName} ${cashInCashOutMethod.accountHolderFirstLastName} ${cashInCashOutMethod.accountHolderSecondLastName}`}
                />
                {cashInCashOutMethod.type !== CashInCashOutType.FLYWIRE && (
                  <ReadOnlyDataRow
                    underlined
                    labelTextId={'ACCOUNT_HOLDER_PARTICIPATION_LEVEL'}
                    value={<Text tId={`${cashInCashOutMethod.accountHolderParticipationLevel}_OPTION`} />}
                  />
                )}
              </Grid>
              <Grid item xs={12} lg={6}>
                {getModuleByCashInCashOutMethodType(cashInCashOutMethod)}
              </Grid>
            </Grid>
            {opened && (
              <BankAccountDetailsEditDialog
                cashInCashOutMethod={cashInCashOutMethod}
                revalidateCashInCashOutMethod={revalidateCashInCashOutMethod}
                opened={opened}
                setOpened={setOpened}
                toggleDialog={toggleDialog}
              />
            )}
          </>
        )}
      </SubSectionWrapper>
    </>
  );
};
